/**
 * Sentry JAVASCRIPT-REACT-2Q — a transport blip while saving HER message used
 * to delete it from history, silently, while the screen said everything worked.
 *
 * ── What she experiences ──────────────────────────────────────────────────
 *
 * `[chat-persist] Failed to save user message: TypeError: Load failed` —
 * WebKit's wording for "the request never completed". The send path is
 * fire-and-forget by design, so nothing about the failure is visible: her
 * message is already on screen (`dispatch({ type: "SEND" })` put it there),
 * Kin's reply streams in normally, and the exchange reads as saved.
 *
 * It isn't. On the next reload the history query returns Kin's answer with no
 * question in front of it. That is worse than an empty thread — a reply to
 * nothing reads as Kin talking to itself, and the thing she actually wrote is
 * the half that is gone.
 *
 * ── Why a retry is the fix, and why postgrest-js will not do it ───────────
 *
 * Same mechanism as #1530's conversation insert, one statement later in the
 * same block. postgrest-js deliberately does not retry a POST:
 * `RETRYABLE_METHODS = ['GET','HEAD','OPTIONS']`, and an insert or upsert is a
 * POST. The rejected fetch is converted to `{ error, status: 0 }` and handed
 * back.
 *
 * ── Why `status === 0` and NOT the message regex ──────────────────────────
 *
 * `src/lib/transportFailure.ts:28-34` names this exact case and rules itself
 * out of it: a PostgREST write failure is a plain object with **no `name`**
 * and the type name buried in the message string, so a `name`- or text-keyed
 * check answers a confident `false` on every one of them. The measurement is
 * in `createConversationWithRetry.ts:28-47`, taken against a real client
 * pointed at an unroutable host.
 *
 * `status === 0` is set in exactly one place — the catch that handles a
 * rejected fetch — so it is the signal, and it is browser- and
 * locale-independent where the wording is neither. `TRANSPORT_STATUS` is
 * imported from `createConversationWithRetry` rather than redeclared: two
 * copies of the one number is how the two writers in this block drift apart.
 *
 * ── ⚠️ RETRYING A POST IS ONLY SAFE IF THE CALLER CARRIES A STABLE KEY ────
 *
 * This is the one thing to know before adding a second caller, because this
 * module cannot enforce it: it runs an opaque thunk and has no way to inspect
 * what that thunk writes.
 *
 * `status === 0` means fetch REJECTED, which is *usually* "it never arrived" —
 * but a connection dropped after the server committed lands here too. Retrying
 * a **plain insert** then writes the row a second time.
 *
 * So the chat caller (`Index.tsx`) mints `crypto.randomUUID()` ONCE, outside
 * the thunk, and upserts on it. A second arrival of a committed row updates the
 * same row with byte-identical content — a no-op — instead of inserting.
 * `public.messages.id` is `uuid PRIMARY KEY DEFAULT gen_random_uuid()` and
 * `Insert` accepts an `id`, which is what makes that possible.
 *
 * ⚠️ **Do not "simplify" that upsert back to an insert, and do not move the
 * mint inside the thunk.** Minting per attempt still reads as an upsert while
 * conflicting with nothing, which is the same bug wearing the fix's clothes.
 * `saveMessageWithRetry.test.ts` guards the shape AND the declaration's
 * position, and both are mutation-tested.
 *
 * ⚠️ Why it had to be closed rather than accepted, since the reasoning is not
 * obvious from the schema: **nothing in the app can delete a single message.**
 * `from("messages")` has no DELETE anywhere in `src/` — only the two inserts
 * and the reads — so a duplicate would be permanent and hers to live with.
 * #1530 accepted the analogous risk on `conversations` because its duplicate
 * was one empty row in a list; a visible line repeated in her transcript, with
 * no way to remove it, is not the same trade. (An earlier draft of this fix did
 * accept it, on the stated grounds that "she can delete one". That was simply
 * false, and it was doing load-bearing work in the argument.)
 *
 * A minted id colliding with an EXISTING row is not a merge risk either: the
 * UPDATE policy is `auth.uid() = user_id`, so another user's row matches zero
 * rows and is reported as a failure rather than silently overwritten.
 *
 * ⚠️ A real database refusal (RLS, a constraint, anything carrying a real HTTP
 * status) is NOT retried. Asking again cannot change a decision the server
 * already made; it only burns time and hides the cause.
 */

import { TRANSPORT_STATUS } from "@/lib/createConversationWithRetry";
import { isTransportFailureMessage } from "@/lib/transportFailure";
import { isAuthNetworkError } from "@/lib/authNetworkError";

/**
 * Longer than `createConversationWithRetry`'s `[250, 750]`, on purpose.
 *
 * That one sits IN FRONT of the Anthropic request — every millisecond there is
 * a millisecond before Kin starts replying, so it is tuned as tight as it can
 * be. This one runs inside an already-detached `void (async () => …)()` with
 * nothing waiting on it, so it can afford to wait long enough to outlast an
 * actual blip (a tunnel, a lift, a handoff between cell and wifi) rather than
 * spending all three attempts inside the same one second of dead signal.
 *
 * ⚠️ Whoever changes these: the budget is free ONLY while nothing awaits this
 * helper. Awaiting it would put ~4s in front of her send.
 */
export const SAVE_RETRY_DELAYS_MS = [1000, 3000];

/**
 * The shape PostgREST returns for `.upsert(...).select("id")` (or an insert —
 * this module does not care which, only that a row comes back).
 *
 * `data` is an ARRAY — no `.single()`. House rule 16 wants "did a row come
 * back" answered directly, and `.single()` answers it by turning zero rows
 * into a `PGRST116` *error*, which would make a silent RLS refusal
 * indistinguishable from a malformed request in the tags.
 */
export interface SaveAttemptResult {
  data: { id: string }[] | null;
  error: unknown;
  status: number;
}

/**
 * `PromiseLike`, not `Promise`: a PostgREST builder is a thenable with no
 * `.catch`/`.finally`, so typing this as `Promise` rejects the very call site
 * it exists for (#1805).
 */
export type SaveAttempt = () => PromiseLike<SaveAttemptResult>;

/** How the failure gets tagged in Sentry. Derived here so the call site cannot drift. */
export type SaveErrorClass = "network" | "database";

export interface SaveOutcome {
  /** True only when a row came back. Never true on a 200-with-no-rows. */
  saved: boolean;
  /** The saved row's id, for the success breadcrumb. Null on failure. */
  id: string | null;
  /** How many attempts ran — 1 when the first one worked. */
  attempts: number;
  /** The final error, for reporting. Null on success. */
  lastError: unknown;
  /** The final status, or null when the attempt threw instead of resolving. */
  lastStatus: number | null;
  /** Null on success. Otherwise the `error_class` tag to report. */
  errorClass: SaveErrorClass | null;
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Classifies a THROWN value, which is the one case with no `status` to read.
 *
 * ⚠️ Order matters, and the first predicate is the one that earns its place.
 * A thrown value here is still INTACT — it has a `name` — which is shape 1 in
 * `transportFailure.ts`, not shape 3. `isAuthNetworkError` is the detector for
 * that shape and it covers cases message-matching cannot:
 *
 *   - `AbortError` — a timeout or an aborted fetch. Its wording is
 *     browser-specific ("The operation was aborted", "signal is aborted without
 *     reason"), matches `TRANSPORT_MESSAGE` nowhere, and `authNetworkError.ts`
 *     says so outright: "not worth matching on". This repo has already seen one
 *     in production, from supabase-js's auth lock `{steal: true}` recovery.
 *   - `AuthRetryableFetchError` with `status: 0`, if a token refresh is what
 *     actually failed underneath the write.
 *
 * Message matching is kept ONLY for a genuinely string-thrown value (`throw
 * "Load failed"`), which is shape 3 and the only thing that predicate is for.
 * `isTransportFailureMessage` returns false on non-strings by design, so the
 * union is safe in either order — but reading the intact shape first is what
 * makes the AbortError case work.
 *
 * Precedent: `Index.tsx`'s title-generate catch does exactly this, for exactly
 * this reason (#1709 task 4).
 *
 * ⚠️ **This union is DUPLICATED, deliberately.** `isLostConnection` in
 * `src/components/BeautyAnalyzerModal.tsx` (#1830) is the same rule for the
 * same question on the Beauty Analyzer recovery path. Both sites shipped the
 * message-only half first and both had to be corrected, so a drift back is the
 * mistake this codebase has already made twice — and it is SILENT, because the
 * site keeps working for a plain dropped connection and stops recovering on an
 * abort with nothing failing.
 *
 * Change one, change the other.
 * `src/lib/__tests__/lostConnectionUnionParity.test.ts` fails if either loses
 * an arm or the two stop matching, and records why there is no shared home yet
 * (a third caller is the signal to extract one).
 */
function isThrownTransportFailure(err: unknown): boolean {
  return isAuthNetworkError(err) || isTransportFailureMessage(err);
}

/**
 * Runs `attempt`, retrying ONLY transport failures, at most `delays.length`
 * extra times. Resolves with an outcome; never rejects.
 *
 * Takes a thunk rather than a Supabase client so the retry policy is testable
 * without a client, and so this module stays clear of PostgREST's generics.
 *
 * ⚠️ The thunk MUST await or return the builder. postgrest-js only issues the
 * request inside `then()`, so a thunk that does `void supabase.from(…)` sends
 * nothing at all and this helper would report a success it never made (#1808).
 */
export async function saveMessageWithRetry(
  attempt: SaveAttempt,
  options: { delays?: readonly number[]; sleep?: (ms: number) => Promise<void> } = {},
): Promise<SaveOutcome> {
  const delays = options.delays ?? SAVE_RETRY_DELAYS_MS;
  const sleep = options.sleep ?? realSleep;

  let lastError: unknown = null;
  let lastStatus: number | null = null;
  let errorClass: SaveErrorClass = "database";
  let attempts = 0;

  for (let i = 0; i <= delays.length; i++) {
    let transport = false;
    attempts = i + 1;

    try {
      const { data, error, status } = await attempt();

      if (!error && data && data.length > 0) {
        return { saved: true, id: data[0].id, attempts, lastError: null, lastStatus: status, errorClass: null };
      }

      // House rule 16: an RLS refusal returns 200 with `error === null` and no
      // rows. Without this clause the zero-row case reads as a clean success
      // and the message is gone with nothing reported — the exact shape the
      // rule exists to stop. Not a transport problem, so not retried.
      lastError =
        error ?? new Error(`user message write returned no rows (status ${status})`);
      lastStatus = status;
      transport = status === TRANSPORT_STATUS;
    } catch (err) {
      // Defensive, and not the measured production shape: postgrest-js converts
      // a rejected fetch into `status: 0` rather than throwing. A throw has no
      // status left to read, so it is CLASSIFIED rather than assumed to be
      // either kind — see `isThrownTransportFailure` for why the intact-error
      // predicate has to come first, and why flattening to a message here
      // would silently fail to retry an AbortError.
      lastError = err;
      lastStatus = null;
      transport = isThrownTransportFailure(err);
    }

    errorClass = transport ? "network" : "database";

    if (!transport) break;
    if (i === delays.length) break;

    await sleep(delays[i]);
  }

  return { saved: false, id: null, attempts, lastError, lastStatus, errorClass };
}
